import { Dispatch, SetStateAction, useCallback } from "react";
import { FiltersTypes } from "@common/types/filters/general";
import { View } from "@common/types/views/views";

interface UseViewChangeHandlerParams<T extends View> {
    setView: Dispatch<SetStateAction<T>>;
    setCachedView: (view: T) => void;
}

/**
 * Layout-only view change handler. Use for callbacks driven by column
 * toggles, reorder, density, or other layout edits where the user is still
 * on the same view and page-level filters must remain untouched.
 */
export function useViewChangeHandler<T extends View>({
    setView,
    setCachedView,
}: UseViewChangeHandlerParams<T>) {
    return useCallback(
        (view: T) => {
            setView(view);
            setCachedView(view);
        },
        [setView, setCachedView],
    );
}

interface UseViewSelectHandlerParams<T extends View> {
    setView: Dispatch<SetStateAction<T>>;
    setCachedView: (view: T) => void;
    setFilters: Dispatch<SetStateAction<Partial<FiltersTypes> | undefined>>;
    defaultFilters: Partial<FiltersTypes>;
}

/**
 * View selection handler. Use when the user is intentionally switching to a
 * different view (saved view selection, reset to base, save modal success,
 * delete fallback). Applies the selected view's filters, merged on top of
 * page defaults.
 */
export function useViewSelectHandler<T extends View>({
    setView,
    setCachedView,
    setFilters,
    defaultFilters,
}: UseViewSelectHandlerParams<T>) {
    return useCallback(
        (view: T) => {
            setView(view);
            setCachedView(view);
            setFilters((prev) => ({
                ...prev,
                ...defaultFilters,
                ...view.filters,
            }));
        },
        [setView, setCachedView, setFilters, defaultFilters],
    );
}
